import { defineStore } from 'pinia'
import { ref } from 'vue'
import { businessInfoService } from '@/services'

export const useBusinessStore = defineStore('business', () => {
  const info = ref(null)
  const loyaltySettings = ref(null)
  const ecomSettings = ref(null)
  const storeSettings = ref(null)
  const tabSettings = ref(null)
  const invoiceSettings = ref(null)
  const loading = ref(false)
  const error = ref(null)

  let fetchInfoInflight = null
  async function fetchInfo({ force = false } = {}) {
    if (!force && fetchInfoInflight) return fetchInfoInflight

    loading.value = true
    error.value = null

    fetchInfoInflight = (async () => {
      try {
        info.value = await businessInfoService.getInfo()
        return info.value
      } catch (err) {
        error.value = err?.message || 'Failed to fetch business info'
        info.value = null
        return null
      } finally {
        loading.value = false
        fetchInfoInflight = null
      }
    })()

    return fetchInfoInflight
  }

  async function updateInfo(data) {
    loading.value = true
    error.value = null
    try {
      const updated = await businessInfoService.updateInfo(data)
      info.value = updated
      return updated
    } catch (err) {
      error.value = err.message || 'Failed to update business info'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updateLogo(logoFile) {
    loading.value = true
    error.value = null
    try {
      const updated = await businessInfoService.updateLogo(logoFile)
      info.value = updated
      return updated
    } catch (err) {
      error.value = err.message || 'Failed to update logo'
      throw err
    } finally {
      loading.value = false
    }
  }

  let fetchLoyaltyInflight = null
  async function fetchLoyaltySettings({ force = false } = {}) {
    if (!force && fetchLoyaltyInflight) return fetchLoyaltyInflight

    loading.value = true
    error.value = null

    fetchLoyaltyInflight = (async () => {
      try {
        loyaltySettings.value = await businessInfoService.getLoyaltySettings()
        return loyaltySettings.value
      } catch (err) {
        error.value = err?.message || 'Failed to fetch loyalty settings'
        loyaltySettings.value = null
        return null
      } finally {
        loading.value = false
        fetchLoyaltyInflight = null
      }
    })()

    return fetchLoyaltyInflight
  }

  async function updateLoyaltySettings(data) {
    loading.value = true
    error.value = null
    try {
      const updated = await businessInfoService.updateLoyaltySettings(data)
      loyaltySettings.value = updated
      return updated
    } catch (err) {
      error.value = err.message || 'Failed to update loyalty settings'
      throw err
    } finally {
      loading.value = false
    }
  }

  let fetchEcomInflight = null
  async function fetchEcomSettings({ force = false } = {}) {
    if (!force && fetchEcomInflight) return fetchEcomInflight

    loading.value = true
    error.value = null

    fetchEcomInflight = (async () => {
      try {
        ecomSettings.value = await businessInfoService.getEcomSettings()
        return ecomSettings.value
      } catch (err) {
        error.value = err?.message || 'Failed to fetch online store settings'
        ecomSettings.value = null
        return null
      } finally {
        loading.value = false
        fetchEcomInflight = null
      }
    })()

    return fetchEcomInflight
  }

  async function updateEcomSettings(data) {
    loading.value = true
    error.value = null
    try {
      const updated = await businessInfoService.updateEcomSettings(data)
      ecomSettings.value = updated
      return updated
    } catch (err) {
      error.value = err.message || 'Failed to update online store settings'
      throw err
    } finally {
      loading.value = false
    }
  }

  let fetchStoreInflight = null
  async function fetchStoreSettings({ force = false } = {}) {
    if (!force && fetchStoreInflight) return fetchStoreInflight

    loading.value = true
    error.value = null

    fetchStoreInflight = (async () => {
      try {
        storeSettings.value = await businessInfoService.getStoreSettings()
        return storeSettings.value
      } catch (err) {
        error.value = err?.message || 'Failed to fetch Blu Store settings'
        storeSettings.value = null
        return null
      } finally {
        loading.value = false
        fetchStoreInflight = null
      }
    })()

    return fetchStoreInflight
  }

  async function updateStoreSettings(data) {
    loading.value = true
    error.value = null
    try {
      const updated = await businessInfoService.updateStoreSettings(data)
      storeSettings.value = updated
      if (info.value) {
        info.value = { ...info.value, ...updated }
      }
      return updated
    } catch (err) {
      error.value = err.message || 'Failed to update Blu Store settings'
      throw err
    } finally {
      loading.value = false
    }
  }

  let fetchTabInflight = null
  async function fetchTabSettings({ force = false } = {}) {
    if (!force && fetchTabInflight) return fetchTabInflight

    loading.value = true
    error.value = null

    fetchTabInflight = (async () => {
      try {
        tabSettings.value = await businessInfoService.getTabSettings()
        return tabSettings.value
      } catch (err) {
        error.value = err?.message || 'Failed to fetch tab settings'
        tabSettings.value = null
        return null
      } finally {
        loading.value = false
        fetchTabInflight = null
      }
    })()

    return fetchTabInflight
  }

  async function updateTabSettings(data) {
    loading.value = true
    error.value = null
    try {
      const updated = await businessInfoService.updateTabSettings(data)
      tabSettings.value = updated
      if (info.value) {
        info.value = { ...info.value, ...updated }
      }
      return updated
    } catch (err) {
      error.value = err.message || 'Failed to update tab settings'
      throw err
    } finally {
      loading.value = false
    }
  }

  let fetchInvoiceInflight = null
  async function fetchInvoiceSettings({ force = false } = {}) {
    if (!force && fetchInvoiceInflight) return fetchInvoiceInflight

    loading.value = true
    error.value = null

    fetchInvoiceInflight = (async () => {
      try {
        invoiceSettings.value = await businessInfoService.getInvoiceSettings()
        return invoiceSettings.value
      } catch (err) {
        error.value = err?.message || 'Failed to fetch invoice settings'
        invoiceSettings.value = null
        return null
      } finally {
        loading.value = false
        fetchInvoiceInflight = null
      }
    })()

    return fetchInvoiceInflight
  }

  async function updateInvoiceSettings(data) {
    loading.value = true
    error.value = null
    try {
      const updated = await businessInfoService.updateInvoiceSettings(data)
      invoiceSettings.value = updated
      if (info.value) {
        info.value = { ...info.value, ...updated }
      }
      return updated
    } catch (err) {
      error.value = err.message || 'Failed to update invoice settings'
      throw err
    } finally {
      loading.value = false
    }
  }

  function hydrateFromBootstrap ({ business = null, loyalty = null } = {}) {
    if (business !== undefined) {
      info.value = business
    }
    if (loyalty !== undefined) {
      loyaltySettings.value = loyalty
    }
    error.value = null
  }

  return {
    info,
    loyaltySettings,
    ecomSettings,
    storeSettings,
    tabSettings,
    invoiceSettings,
    loading,
    error,
    fetchInfo,
    updateInfo,
    updateLogo,
    fetchLoyaltySettings,
    updateLoyaltySettings,
    fetchEcomSettings,
    updateEcomSettings,
    fetchStoreSettings,
    updateStoreSettings,
    fetchTabSettings,
    updateTabSettings,
    fetchInvoiceSettings,
    updateInvoiceSettings,
    hydrateFromBootstrap
  }
})
